<template>
  <div
    v-if="!!tender && tender.tenderId"
    data-qa="compare-lot-groups">
    <div
      class="h4-headline text-start pb-5 pt-4"
      data-qa="compare-proposals-count">
      {{ bids.length }} {{ $tc('Tenders.Proposals', bids.length ) }}
    </div>
    <Groups
      can_expand
      group-key="key"
      :groups="groups"
      small
      :watch-changed-groups="keepExpandedGroups"
      with_title>
      <template #title="{ group }">
        <div class="flex gap-3 align-center">
          <LotGroupTitle
            data-qa="compare-lot-group-title"
            :description="group.description"
            :title="group.title"/>
          <span v-if="group && group.positions"
                class="opacity-6 flex gap-3 ml-3">
            {{ group.positions.length }} {{ $tc('Common.position', group.positions.length) }}
          </span>
        </div>
      </template>
      <template #content="{ group }">
        <div
          class="pb-4 text-left"
          data-qa="compare-lot-group">
          <v-simple-table
            id="compare-lot-group-table"
            class="main__green"
            data-qa="compare-lot-group-table">
            <template #default>
              <thead>
                <tr class="header">
                  <th class="modal-column cell secondary__green_chosen">
                    <div class="header-cell"/>
                  </th>
                  <th
                    v-for="column in group.columns"
                    :key="column.supplierKey"
                    class="organisation-cell cell px-2 h7-text-medium secondary__green_chosen black--text"
                    data-qa="compare-supplier">
                    <div class="header-cell">
                      <div class="flex flex-wrap align-center">
                        <v-checkbox
                          v-if="canEdit && !column.placeholder"
                          v-model="selected"
                          class="ma-0 pa-0"
                          color="var(--black-color)"
                          data-qa="compare-supplier-checkbox"
                          hide-details
                          :ripple="false"
                          :value="column.itemKey"/>
                        <div>
                          <div>
                            <BidderIsWinner
                              v-if="column.item && column.item.selectedBid"
                              class="my-auto mr-2"
                              :color="column.item.hasPositionNotSelectedBid ? '#ddd309' : '#7DE199'"/>
                            <span data-qa="compare-supplier-name">{{ column.bidderInfo ? column.bidderInfo.organizationName : '' }}</span>
                            <ExternalParticipantChip
                              v-if="column.foreignBidderBid"
                              class="ml-3"
                              data-qa="compare-supplier-external"/>
                          </div>
                          <div class="opacity-6 flex gap-3">
                            {{ $t('Tenders.Rating') }}: {{ column.bidderInfo.rating || '–' }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(position, positionIndex) in group.positions">
                  <tr
                    :key="`${group.key}_${position.lotId}`"
                    class="position-title"
                    data-qa="compare-position-title">
                    <td
                      class="text-start">
                      #{{ positionIndex + 1 }} {{ position.title }}
                    </td>
                    <td
                      v-for="(cell, cellIndex) in position.cells"
                      :key="cellIndex"
                      class="text-start info-cell cell"
                      data-qa="compare-value">
                      <BidderIsWinner
                        v-if="cell && cell.selectedBid"
                        class="my-auto"/>
                    </td>
                  </tr>
                  <tr
                    v-for="key in positionKeys"
                    :key="`${group.key}_${position.lotId}_${key.value}_${key.text}`"
                    class="info-row"
                    :data-qa="`compare-position-row-${key.value}`">
                    <td
                      class="text-start modal-column cell"
                      data-qa="compare-row-label">
                      {{ key.text }}
                    </td>
                    <td
                      v-for="(cell, cellIndex) in position.cells"
                      :key="cellIndex"
                      class="text-start info-cell cell"
                      :class="{'bold': key.value == 'price' }"
                      data-qa="compare-value">
                      <ComparativeTableCell
                        v-if="cell"
                        :field="key"
                        :item="cell"
                        :muted="$_getSame(key.value, position.cells.filter(Boolean))"
                        :tender="tender"/>
                      <span
                        v-else
                        data-qa="compare-value-empty">–</span>
                    </td>
                  </tr>
                </template>
                <tr
                  class="position-title"
                  data-qa="compare-lot-total">
                  <td
                    class="text-start h7-text-medium"
                    :colspan="group.columns.length + 1">
                    {{ $t('Tenders.CompareLotTotal') }}
                  </td>
                </tr>
                <tr
                  v-for="key in summaryKeys"
                  :key="`${group.key}_total_${key.value}_${key.text}`"
                  class="info-row"
                  :data-qa="`compare-lot-total-row-${key.value}`">
                  <td
                    class="text-start modal-column cell"
                    data-qa="compare-row-label">
                    {{ key.text }}
                  </td>
                  <td
                    v-for="(cell, cellIndex) in group.summary"
                    :key="cellIndex"
                    class="text-start info-cell cell"
                    :class="{'bold': key.value == 'price' }"
                    data-qa="compare-value">
                    <ComparativeTableCell
                      v-if="cell"
                      :field="key"
                      :item="cell"
                      :muted="$_getSame(key.value, group.summary.filter(Boolean))"
                      :tender="tender"/>
                    <span
                      v-else
                      data-qa="compare-value-empty">–</span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <sDivider
            class="mt-4"
            color="var(--secondary__green_not_active)"/>
        </div>
      </template>
    </Groups>
  </div>
</template>
<script>
import Groups from 'components/common/blocks/groups/Groups.vue'
import BidderIsWinner from 'components/tenders/BidderIsWinner.vue'
import ComparativeTableCell from 'pages/tender item/ComparativeTableCell.vue'
import LotGroupTitle from 'components/tenders/tender page/tender tabs/tables/LotGroupTitle.vue'
import ExternalParticipantChip from 'components/tenders/tender page/ExternalParticipantChip.vue'

import { tenderMixin, snackbarMixin, modalMixin } from 'mixins'
import comparativeTableMixin from 'mixins/comparativeTableMixin'
import { lotGroupSupplierKey, lotGroupItemKey } from 'helpers/lotGroupPositions'

/**
 * Порівняльна таблиця для режиму лот-позицій: одна таблиця на лот (групу позицій),
 * колонки — постачальники, блок рядків на кожну позицію і підсумок по лоту.
 * bids — агреговані рядки «постачальник × лот» з tenderMixin.$_getGroupsPositionsByLot
 */
export default {
  name: 'ComparativeTableLotGroups',
  mixins: [ tenderMixin, snackbarMixin, modalMixin, comparativeTableMixin ],
  data() {
    return {
      criteryCard: null,
    }
  },
  methods: {
    /**
     * Groups при кожному перерахунку groups розгортає всі лоти — залишаємо згорнутими ті,
     * що користувач згорнув; нові лоти розгортаються
     */
    keepExpandedGroups(old, groups, expanded) {
      // окремий масив: Groups робить splice по expanded при згортанні — інакше лот зник би з groups
      if (!old) {
        return [...groups]
      }
      return groups.filter(group => expanded.some(item => item.key === group.key) || !old.some(item => item.key === group.key))
    },
    supplierKey(item) {
      return lotGroupSupplierKey(item)
    },
    /** підсумок лоту в полях, які розуміє ComparativeTableCell */
    toSummaryCell(item) {
      const first = item.positions?.[0] || {}
      return {
        ...item,
        currencyPrice: item.price,
        summ: item.totalCost,
        currencySumm: item.totalCost,
        currency: first.currency,
        remark: first.remark,
        all_criteria: (item.all_criteria || []).filter(group => group.name === 'ToTender'),
      }
    },
    /** критерії до тендеру показуються в підсумку лоту, тут — лише критерії позиції */
    toPositionCell(position) {
      return {
        ...position,
        all_criteria: (position.all_criteria || []).filter(group => group.name !== 'ToTender'),
      }
    },
    /**
     * Після вибору/зняття переможця ознаки selectedBid / hasPositionNotSelectedBid / bestPrice
     * лотів і позицій розраховує бекенд — просимо сторінку перечитати тендер і переформувати групи
     */
    afterCompareAction() {
      this.$emit('changeWinners')
    },
    /**
     * Чекбокси прив'язані до ключа «учасник × лот», а не до об'єкта: VInput (Vuetify 2) записує
     * свій value у lazyValue при його зміні — після перечитування тендеру (нові об'єкти) модель
     * чекбокса переставала бути масивом і вибір ламався. Для snackbar ключі → агреговані рядки
     */
    selectedToElements(keys) {
      return keys.map(key => this.bids.find(item => lotGroupItemKey(item) === key)).filter(Boolean)
    },
  },
  computed: {
    compareSnackbarOptions() {
      return { isLotGroups: true }
    },
    suppliers() {
      const result = []
      this.bids.forEach(item => {
        const key = this.supplierKey(item)
        if (!result.some(supplier => supplier.key === key)) {
          result.push({ key, bidderInfo: item.bidderInfo, foreignBidderBid: item.foreignBidderBid ?? item.positions?.[0]?.foreignBidderBid })
        }
      })
      return result
    },
    allPositions() {
      return this.bids.flatMap(item => item.positions || [])
    },
    groups() {
      const byGroup = this.$_.groupBy(this.bids, item => item.groupId ?? item.lotGroupNumber)

      return this.$_.sortBy(Object.entries(byGroup).map(([key, items]) => {
        const first = items[0]
        const columns = this.suppliers.map(supplier => {
          const item = items.find(it => this.supplierKey(it) === supplier.key)
          return {
            supplierKey: supplier.key,
            bidderInfo: supplier.bidderInfo,
            foreignBidderBid: supplier.foreignBidderBid,
            placeholder: !item,
            item: item || null,
            itemKey: item ? lotGroupItemKey(item) : null,
          }
        })

        const lots = this.$_.sortBy(
          this.$_.uniqBy(items.flatMap(item => item.positions || []), 'lotId'),
          'lotNumber'
        )
        const positions = lots.map(lot => ({
          lotId: lot.lotId,
          title: lot.lotTitleOrig || lot.lotTitle,
          cells: columns.map(column => {
            const position = column.item?.positions?.find(p => p.lotId === lot.lotId)
            return position ? this.toPositionCell(position) : null
          }),
        }))

        return {
          key,
          order: first.lotGroupNumber || 0,
          title: `#${first.lotGroupNumber || ''} ${first.lotGroupTitle || ''}`.trim(),
          description: this.tender.lotGroups?.find(lotGroup => lotGroup.id === first.groupId)?.description || '',
          columns,
          positions,
          summary: columns.map(column => column.item ? this.toSummaryCell(column.item) : null),
        }
      }), 'order')
    },
    positionKeys() {
      const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1)
      }

      let keys = [
        {
          text: this.tender.biddingType.tenderingForPercentage ? this.$t('Tenders.DiscountPercentage') : this.$t('Catalog.Price'),
          value: 'price'
        },
        {
          text: this.$t('Catalog.Amount'),
          value: 'quantity'
        },
      ]   
      if (this.show_reducedPrice && !this.hide_price) {
        keys.push({
          text: capitalizeFirstLetter(this.$t('Tenders.GivenPrice')),
          value: 'reducedPrice'
        })
      }
      keys = [
        ...keys,
        {
          text: this.$t('Catalog.TotalCost'),
          value: 'summ'
        },
        {
          text: this.$t('Tenders.AdditionalInfoPosition'),
          value: 'additionalInfo'
        }
      ]
      return keys.concat(
        this.$_defaultCriteriaKeys(this.allPositions),
        this.$_criteriaKeys(this.allPositions, ['ToLot'])
      )
    },
    hide_price() {
      return this.tender?.biddingType?.isMultistage && this.$can('hide', 'PricesOnPrequalification') && this.tender.tenderStatus?.id == this.statuseKeys.PREQUALIFICATION
    },
    show_reducedPrice() {
      return this.tender?.biddingType?.reducedPriceAvailable && this.$can('use', 'useCriteriaCards') && this.criteryCard?.useReducedPrice
    },
    summaryKeys() {
      const keys = [
        {
          text: this.tender.biddingType.tenderingForPercentage ? this.$t('Tenders.DiscountPercentage') : this.$t('Tenders.LotPrice'),
          value: 'price'
        },
        {
          text: this.$t('Catalog.TotalCost'),
          value: 'summ'
        },
        {
          text: this.$t('Tenders.DateOfSubmission'),
          value: 'timeSupplyBids'
        },
        {
          text: this.$t('Tenders.AdditionalInfoTEnder'),
          value: 'remark'
        },
      ]
      return keys.concat(this.$_criteriaKeys(this.allPositions, ['ToTender']))
    },
  },
  watch: {
    'tender.criteriaCard': {
      async handler(id) {
        this.criteryCard = id ? await this.$store.dispatch('infoAboutCriterionCard', { save: true, id }) || null : null
      },
      immediate: true,
    },
  },
  components: {
    Groups,
    LotGroupTitle,
    BidderIsWinner,
    ComparativeTableCell,
    ExternalParticipantChip,
  },
  props: {
    tender: {
      type: Object,
      required: true
    },
    /** агреговані рядки «постачальник × лот» */
    bids: {
      type: Array,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
  },
}
</script>
<style lang="scss" scoped>
.position-title td {
  padding: 12px 14px !important;
  font-weight: 600;
  font-size: 14px;
  font-size: 15px !important;
  background-color: var(--v-secondary__green_chosen-base) !important;
  border-bottom: thin solid var(--v-main__green-base) !important;
}
.header-cell {
  display: grid;
  grid-template-rows: 1fr auto auto;
}
.modal-column {
  width: 200px;
  max-width: 200px;
  min-width: 200px;
}
</style>